// ===========================================================================
// Executive Financial Dashboard (Module K) — capa de datos.
//
// Cruza el costo médico real de muestra (DataDashCEO_v2/cost) con el modelo
// financiero LAB8R fake (fakeData.js). Cuando exista el schema `finance`,
// los loaders se reemplazan por los endpoints anotados en fakeData.js.
// ===========================================================================

import {
  MLR_TARGET,
  FAKE_MLR_BY_MONTH,
  FAKE_FINANCIALS,
  FAKE_DRIVER_TRENDS,
  FAKE_COST_BREAKDOWN,
  FAKE_REVENUE_LEVERS,
} from "./fakeData";

const FALLBACK_MEMBERS = 10462;

/** Mapa costType (cost/summary.json) → driver del dashboard. */
const DRIVER_MAP = [
  { match: "Inpatient", key: "inpatient", label: "Inpatient" },
  { match: "Emergency", key: "er", label: "Emergency Room" },
  { match: "Outpatient", key: "outpatient", label: "Outpatient / Specialty" },
  { match: "Professional", key: "professional", label: "Professional" },
  { match: "Pharmacy", key: "pharmacy", label: "Pharmacy" },
];

/** Breakdown de costo real (claims-based cost of care). */
async function loadCostSummary() {
  try {
    const mod = await import(
      "../../../pages/Main/Temp/DataDashCEO_v2/cost/summary.json"
    );
    const data = mod.default || mod;
    return Array.isArray(data.breakdown) ? data.breakdown : FAKE_COST_BREAKDOWN;
  } catch (e) {
    return FAKE_COST_BREAKDOWN;
  }
}

/** Total de miembros real para el cálculo de PMPM. */
async function loadTotalMembers() {
  try {
    const mod = await import(
      "../../../pages/Main/Temp/DataDashCEO_v2/population/overview.json"
    );
    const data = mod.default || mod;
    return data.totalMembers || FALLBACK_MEMBERS;
  } catch (e) {
    return FALLBACK_MEMBERS;
  }
}

/**
 * Ensambla el dataset ejecutivo completo.
 *
 * @returns {Promise<Object>} {
 *   signals: { mlr, mlrTarget, netMarginPct, revenuePmpm, costPmpm, deficitYtd, riskScore },
 *   mlrTrend: [{ month, mlr }],
 *   costDrivers: [{ key, label, annual, pctOfTotal, pmpm, trendPct }],
 *   revenueLevers: [{ key, label, module, annualOpportunity, capturedYtd, capturedPct }],
 *   totalCost, members
 * }
 */
export async function getExecutiveFinancials() {
  const [breakdown, members] = await Promise.all([loadCostSummary(), loadTotalMembers()]);

  // Drivers de costo: $ reales + PMPM derivado + tendencia fake (LAB8R pendiente).
  const detail = breakdown.filter((b) => b.costType !== "Cost of Care");
  const mapped = DRIVER_MAP.map((d) => {
    const row = detail.find((b) => b.costType.includes(d.match));
    return {
      key: d.key,
      label: d.label,
      annual: row?.cost || 0,
      pctOfTotal: row?.percentage || 0,
    };
  });
  const mappedTypes = DRIVER_MAP.map((d) => d.match);
  const otherRows = detail.filter((b) => !mappedTypes.some((m) => b.costType.includes(m)));
  mapped.push({
    key: "other",
    label: "Other (ancillary, virtual)",
    annual: otherRows.reduce((acc, b) => acc + (b.cost || 0), 0),
    pctOfTotal: otherRows.reduce((acc, b) => acc + (b.percentage || 0), 0),
  });

  const costDrivers = mapped.map((d) => ({
    ...d,
    pmpm: d.annual / 12 / members,
    trendPct: FAKE_DRIVER_TRENDS[d.key] ?? 0,
  }));

  const totalCost = costDrivers.reduce((acc, d) => acc + d.annual, 0);
  const costPmpm = totalCost / 12 / members;

  const revenueLevers = FAKE_REVENUE_LEVERS.map((l) => ({
    ...l,
    capturedPct: (l.capturedYtd / l.annualOpportunity) * 100,
  }));

  return {
    signals: {
      mlr: FAKE_FINANCIALS.mlr,
      mlrTarget: MLR_TARGET,
      netMarginPct: FAKE_FINANCIALS.netMarginPct,
      revenuePmpm: FAKE_FINANCIALS.revenuePmpm,
      costPmpm,
      deficitYtd: FAKE_FINANCIALS.deficitYtd,
      riskScore: FAKE_FINANCIALS.riskScore,
    },
    mlrTrend: FAKE_MLR_BY_MONTH,
    costDrivers,
    revenueLevers,
    totalCost,
    members,
  };
}

/** Formato compacto en millones para señales ejecutivas: −$7.14M */
export const fmtMillions = (n) =>
  `${n < 0 ? "−" : ""}$${Math.abs(n / 1e6).toFixed(2)}M`;
